import React from "react";
import mapSvg from "../../assets/map.svg";

// City hub definitions with exact coordinates mapped to 1024x1024 SVG viewBox
const hubs = [
  { id: "delhi", name: "DELHI NCR", x: 375, y: 315, textX: 395, textY: 320 },
  { id: "ahmedabad", name: "AHMEDABAD", x: 285, y: 515, textX: 305, textY: 520 },
  { id: "mumbai", name: "MUMBAI", x: 325, y: 665, textX: 345, textY: 670 },
  { id: "pune", name: "PUNE", x: 358, y: 710, textX: 378, textY: 715 },
  { id: "hyderabad", name: "HYDERABAD", x: 450, y: 745, textX: 470, textY: 750 },
  { id: "bengaluru", name: "BENGALURU", x: 418, y: 885, textX: 438, textY: 890 },
];

// Curved connecting paths between hubs
const routes = [
  // Delhi NCR -> Mumbai
  {
    id: "delhi-mumbai",
    d: "M 375 315 Q 390 490 325 665",
    dur: "3.2s",
  },
  // Delhi NCR -> Hyderabad
  {
    id: "delhi-hyderabad",
    d: "M 375 315 Q 405 530 450 745",
    dur: "3.4s",
  },
  // Ahmedabad -> Mumbai
  {
    id: "ahmedabad-mumbai",
    d: "M 285 515 Q 300 590 325 665",
    dur: "2.4s",
  },
  // Mumbai -> Pune
  {
    id: "mumbai-pune",
    d: "M 325 665 Q 340 685 358 710",
    dur: "1.8s",
  },
  // Pune -> Hyderabad
  {
    id: "pune-hyderabad",
    d: "M 358 710 Q 400 735 450 745",
    dur: "2.6s",
  },
  // Hyderabad -> Bengaluru
  {
    id: "hyderabad-bengaluru",
    d: "M 450 745 Q 450 815 418 885",
    dur: "2.8s",
  },
];

function InfrastructureMap({
  dark = false,
  tag = "PAN-INDIA INFRASTRUCTURE",
  title = "Hubs that move paper, not just pins on a map.",
  description = "Delhi NCR, Mumbai, Pune, Ahmedabad, Hyderabad and Bengaluru feed a single routing layer. Leads clear wherever the DSA is standing.",
  showRealTimeCard = true,
  showCityLabels = true,
  badgeText = "Live in 40+ cities",
}) {
  return (
    <section
      className={`overflow-hidden section-spacing ${
        dark ? "bg-[#191e1c]" : "bg-white"
      }`}
    >
      <div className="section-container">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14 xl:gap-16">
          {/* ================= LEFT CONTENT ================= */}
          <div className="lg:col-span-5">
            {/* Label */}
            <p className={`font-jakarta text-[11px] sm:text-[12px] font-medium tracking-[0.34em] uppercase mb-2.5 ${dark ? "text-[var(--mint-green)]" : "text-[var(--hero-green)]"}`}>
              {tag}
            </p>

            {/* Headline */}
            <h2
              className={`mt-3 font-bricolage text-[30px] sm:text-[36px] md:text-[40px] font-bold leading-[1.12] tracking-[-1px] ${
                dark ? "text-white" : "text-[#111827]"
              }`}
            >
              {title}
            </h2>

            {/* Description */}
            <p
              className={`mt-4 font-jakarta text-[15px] leading-[1.65] sm:mt-5 sm:text-[16px] ${
                dark ? "text-[#9fa9a5]" : "text-[#556369]"
              }`}
            >
              {description}
            </p>

            {/* Real-time routing pill indicator (Optional) */}
            {showRealTimeCard && (
              <div className="mt-8 sm:mt-10">
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--mint-green)]" />
                  <span className="font-jakarta text-[11px] font-medium tracking-[0.24em] text-[var(--mint-green)] uppercase">
                    REAL-TIME ROUTING
                  </span>
                </div>

                <div className="mt-3.5 inline-flex items-center gap-3 rounded-full border border-[#e2e9e5] bg-white px-5 py-2.5 shadow-[0_10px_25px_rgba(0,0,0,0.03)] transition-transform duration-200 hover:scale-[1.02]">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--mint-green)] opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[var(--mint-green)]" />
                  </span>
                  <p className="font-jakarta text-[13px] font-medium text-[#0b1720] sm:text-[14px]">
                    Lead matched · Pune
                  </p>
                  <span className="font-jakarta text-[12px] text-[#86958e]">
                    2 min ago
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* ================= RIGHT MAP DISPLAY ================= */}
          <div className="relative flex items-center justify-center lg:col-span-7">
            <div className="relative aspect-square w-full max-w-[660px]">
              
              {/* Top Floating Badge: Live in 40+ cities (Sharp, Prominent & Elevated) */}
              <div className="absolute top-2 left-4 sm:top-5 sm:left-8 z-20 inline-flex items-center gap-2.5 rounded-full border border-[var(--hero-green-border)] bg-white/95 px-4 py-2 sm:px-5 sm:py-2.5 shadow-[0_10px_30px_rgba(0,135,81,0.12)] backdrop-blur-md transition-all hover:scale-105">
                <span className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--hero-green)] opacity-75" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-[var(--hero-green)]" />
                </span>
                <span className="font-jakarta text-[13px] sm:text-[14.5px] font-bold text-[#111827]">
                  {badgeText}
                </span>
              </div>

              {/* Base SVG Map */}
              <img
                src={mapSvg}
                alt="Finverse Pan-India Infrastructure Map"
                className={`h-full w-full select-none object-contain ${
                  dark ? "opacity-90 brightness-110" : "opacity-95"
                }`}
                loading="lazy"
                decoding="async"
              />

              {/* Interactive Clean Network Overlay matching map.svg color */}
              <svg
                viewBox="0 0 1024 1024"
                className="pointer-events-none absolute inset-0 h-full w-full select-none"
                aria-hidden="true"
              >
                {/* Clean Connecting Route Lines matching map.svg */}
                {routes.map((route) => (
                  <path
                    key={route.id}
                    d={route.d}
                    fill="none"
                    stroke="#2EE5A2"
                    strokeWidth="2.5"
                    strokeOpacity="0.9"
                    strokeLinecap="round"
                  />
                ))}

                {/* Animated Travelling Clean Particles */}
                {routes.map((route) => (
                  <g key={`particle-${route.id}`}>
                    {/* Primary clean mint pulse */}
                    <circle
                      r="6"
                      fill="#2EE5A2"
                    >
                      <animateMotion
                        path={route.d}
                        dur={route.dur}
                        repeatCount="indefinite"
                      />
                    </circle>

                    {/* Secondary bright center core */}
                    <circle r="3" fill="#ffffff">
                      <animateMotion
                        path={route.d}
                        dur={route.dur}
                        repeatCount="indefinite"
                      />
                    </circle>
                  </g>
                ))}

                {/* City Hub Nodes */}
                {hubs.map((hub) => (
                  <g key={hub.id} className="pointer-events-auto">
                    {/* Outer pulsating aura */}
                    <circle
                      cx={hub.x}
                      cy={hub.y}
                      r="22"
                      fill="#2EE5A2"
                      fillOpacity={dark ? "0.3" : "0.2"}
                    >
                      <animate
                        attributeName="r"
                        values="14;28;14"
                        dur="2.4s"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="fill-opacity"
                        values={dark ? "0.35;0.05;0.35" : "0.28;0.04;0.28"}
                        dur="2.4s"
                        repeatCount="indefinite"
                      />
                    </circle>

                    {/* Core Hub Ring */}
                    <circle
                      cx={hub.x}
                      cy={hub.y}
                      r="10"
                      fill="#ffffff"
                      stroke="#2EE5A2"
                      strokeWidth="3"
                    />

                    {/* Inner pinpoint dot */}
                    <circle
                      cx={hub.x}
                      cy={hub.y}
                      r="4.5"
                      fill="#2EE5A2"
                    />

                    {/* Hub Name Label (Bold, High Contrast Outline) */}
                    {showCityLabels && (
                      <text
                        x={hub.textX}
                        y={hub.textY + 2}
                        fill={dark ? "#ffffff" : "#111827"}
                        fontFamily="'Plus Jakarta Sans', sans-serif"
                        fontSize="15"
                        fontWeight="800"
                        letterSpacing="1.2"
                        style={{
                          paintOrder: "stroke",
                          stroke: dark ? "#191e1c" : "#ffffff",
                          strokeWidth: "5px",
                          strokeLinejoin: "round",
                        }}
                      >
                        {hub.name}
                      </text>
                    )}
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default InfrastructureMap;
